// ============================================================================
// PublicationComposer — composer du fil social (E1b, ecritures)
// ============================================================================
// E1a livrait la bande composer INERTE ; E1b la rend interactive : replie =
// bande avatar + placeholder + « Publier » ; deplie = formulaire inline
// (titre, message, categorie, visibilite) dans la meme carte. La soumission
// est OPTIMISTE (usePublishPublication prepend le fil + rollback) : on replie
// immediatement, l'erreur remonte en code court au bandeau de la page
// (onError — mappe gest:accueil.errors.*, jamais de code brut, GA-21).
// La visibilite offerte ici n'est qu'une AFFORDANCE : le serveur re-resout
// assignableVisibilities contre le role de SESSION (bind-to-fetch).

import { useState } from 'react';
import { GsAvatar, GS_COMPONENTS } from '@gs/ui';
import type { TFunction } from 'i18next';
import { PUBLICATION_CATEGORIES, accueilErrorCode, type PublicationCategory } from '../../hooks/useAccueil';
import { usePublishPublication, type OptimisticAuthor } from '../../hooks/useAccueilMutations';

const INPUT_DARK = 'dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100';
type Visibility = 'public' | 'management';

export function PublicationComposer({
  author,
  firstName,
  t,
  onError,
}: {
  author: OptimisticAuthor;
  firstName: string;
  t: TFunction;
  /** Remonte le code court d'erreur au bandeau de la page (GA-21). */
  onError: (code: string) => void;
}) {
  const publish = usePublishPublication(author);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<PublicationCategory>('general');
  const [visibility, setVisibility] = useState<Visibility>('public');

  const canSubmit = title.trim().length > 0 && body.trim().length > 0 && !publish.isPending;

  function reset() {
    setTitle('');
    setBody('');
    setCategory('general');
    setVisibility('public');
  }

  function submit() {
    if (!canSubmit) return;
    // Optimiste : replier tout de suite — le fil montre deja la publication ;
    // un refus serveur retire l'entree (rollback) + bandeau d'erreur.
    publish.mutate(
      { title: title.trim(), body: body.trim(), category, visibility },
      { onError: (err) => onError(accueilErrorCode(err)) },
    );
    reset();
    setOpen(false);
  }

  if (!open) {
    // Affordance app-locale (faux « placeholder » cliquable) — acceptable BL-18/B-4 ;
    // ne pas forcer un token input (ce n'est pas un champ).
    return (
      <div className="mb-4 flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <GsAvatar name={author.name ?? author.email} size="sm" />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="min-w-0 flex-1 truncate text-left text-body text-slate-400 transition-colors hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
        >
          {t('gest:accueil.composer.placeholder', { name: firstName })}
        </button>
        <button type="button" onClick={() => setOpen(true)} className={GS_COMPONENTS.btnAction}>
          {t('gest:accueil.composer.publish')}
        </button>
      </div>
    );
  }

  return (
    <form
      className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      aria-label={t('gest:accueil.composer.formTitle')}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex items-center gap-2.5">
        <GsAvatar name={author.name ?? author.email} size="sm" />
        <h3 className="text-body font-semibold text-slate-800 dark:text-slate-100">
          {t('gest:accueil.composer.formTitle')}
        </h3>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="composer-title" className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:accueil.composer.titleLabel')}
        </label>
        <input
          id="composer-title"
          autoFocus
          maxLength={300}
          placeholder={t('gest:accueil.composer.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="composer-body" className="text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:accueil.composer.bodyLabel')}
        </label>
        <textarea
          id="composer-body"
          rows={4}
          maxLength={10000}
          placeholder={t('gest:accueil.composer.bodyPlaceholder')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className={`${GS_COMPONENTS.input} ${INPUT_DARK}`}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="composer-category" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:accueil.composer.categoryLabel')}
          </label>
          <select
            id="composer-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as PublicationCategory)}
            className={`${GS_COMPONENTS.input} ${INPUT_DARK} w-48`}
          >
            {PUBLICATION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`gest:accueil.categories.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="composer-visibility" className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:accueil.composer.visibilityLabel')}
          </label>
          {/* Affordance seulement — le serveur re-valide contre le role de session. */}
          <select
            id="composer-visibility"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as Visibility)}
            className={`${GS_COMPONENTS.input} ${INPUT_DARK} w-48`}
          >
            <option value="public">{t('gest:accueil.composer.visibilityPublic')}</option>
            <option value="management">{t('gest:accueil.composer.visibilityManagement')}</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
          }}
          className={`${GS_COMPONENTS.btnSecondary} touch-manipulation`}
        >
          {t('gest:accueil.composer.cancel')}
        </button>
        <button type="submit" disabled={!canSubmit} className={GS_COMPONENTS.btnAction}>
          {t('gest:accueil.composer.publish')}
        </button>
      </div>
    </form>
  );
}
